/**
 * FE-local DTOs for platform map custom views (ADR-MV-05 / EV-G3-WIRE).
 * Consumed by account API + shops Views menu — ban duplicate under features/shops.
 *
 * EV-G4-VIEW-ALIAS: no bare `View` type/alias.
 * EV-G1-PATCH-KIND: export per-kind Patch*Body + BodyForKind<K> only —
 * do **not** export an untagged `PatchMapCustomViewBody` union for call sites.
 */

/** Post-normalize row — Decimal geometry fields are number | null. */
export type MapCustomViewDTO = {
  id: number;
  kind: 'around_me' | 'around_place' | 'map_camera' | 'exact_bounds';
  name: string;
  radiusKm: number | null;
  centerLat: number | null;
  centerLng: number | null;
  placeLabel: string | null;
  swLat: number | null;
  swLng: number | null;
  neLat: number | null;
  neLng: number | null;
  createdAt: string;
  updatedAt: string;
};

/**
 * Pre-normalize wire shape (docs only) — Prisma JSON may stringify Decimals.
 * Runtime entry for normalize is `unknown` (EV-G2-NORMALIZE).
 */
export type MapCustomViewWire = Omit<
  MapCustomViewDTO,
  'radiusKm' | 'centerLat' | 'centerLng' | 'swLat' | 'swLng' | 'neLat' | 'neLng'
> & {
  radiusKm: number | string | null;
  centerLat: number | string | null;
  centerLng: number | string | null;
  swLat: number | string | null;
  swLng: number | string | null;
  neLat: number | string | null;
  neLng: number | string | null;
};

/** POST — kind discriminant + per-kind allowlist (plan table). */
export type CreateMapCustomViewBody =
  | {
      kind: 'around_me';
      name: string;
      radiusKm: number;
      centerLat: number;
      centerLng: number;
    }
  | {
      kind: 'around_place';
      name: string;
      radiusKm: number;
      centerLat: number;
      centerLng: number;
      placeLabel: string;
    }
  | {
      kind: 'map_camera' | 'exact_bounds';
      name: string;
      swLat: number;
      swLng: number;
      neLat: number;
      neLng: number;
    };

/**
 * PATCH — kind forbidden (→422). Per-kind geometry only; opposite-kind
 * type-excluded (EV-G1-PATCH-KIND). No `kind` on body.
 */
export type PatchMapCustomViewAroundMeBody = {
  name?: string;
  radiusKm?: number;
  centerLat?: number;
  centerLng?: number;
};

export type PatchMapCustomViewAroundPlaceBody = {
  name?: string;
  radiusKm?: number;
  centerLat?: number;
  centerLng?: number;
  /** Clearable only — EV-G7-PATCH-EMPTY (`null` clears; omit keeps prior). */
  placeLabel?: string | null;
};

export type PatchMapCustomViewBoundsBody = {
  name?: string;
  swLat?: number;
  swLng?: number;
  neLat?: number;
  neLng?: number;
};

/**
 * EV-G1-PATCH-KIND — call sites must pass BodyForKind<K> with persisted kind K.
 * Ban flat bag / opposite-kind fields / `kind` on Patch.
 */
export type PatchMapCustomViewBodyForKind<K extends MapCustomViewDTO['kind']> =
  K extends 'around_me'
    ? PatchMapCustomViewAroundMeBody
    : K extends 'around_place'
      ? PatchMapCustomViewAroundPlaceBody
      : PatchMapCustomViewBoundsBody;

/** Platform error codes from create/patch (cap / unique name). */
export const MAP_VIEW_ERROR_CODES = {
  VIEW_LIMIT_EXCEEDED: 'VIEW_LIMIT_EXCEEDED',
  VIEW_NAME_CONFLICT: 'VIEW_NAME_CONFLICT',
} as const;

export type MapViewErrorCode =
  (typeof MAP_VIEW_ERROR_CODES)[keyof typeof MAP_VIEW_ERROR_CODES];

/** Nominatim hit / search result SSOT: `./mapViewsNominatim` (re-exported from mapViewsApi). */
